import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { Box, ButtonBase, Chip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { BaseButton } from 'components/BaseButton';
import { ListingEntry } from 'graphql/listings/queries';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PartyList } from '../hooks';
import { formatRoleName } from '../utils';
import PartyItem from './PartyItem';

const useStyles = makeStyles((theme: any) => ({
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(1),
    '@media (hover: none) and (pointer: coarse)': {
      '& .MuiButton-root': {
        minHeight: theme.spacing(5.5),
      },
    },
  },
  group: {
    '& + &': {
      marginTop: theme.spacing(2),
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    width: '100%',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0.5),
    borderRadius: 4,
    textAlign: 'left',
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
    '@media (hover: none) and (pointer: coarse)': {
      minHeight: theme.spacing(5.5),
    },
  },
  headerSelected: {
    backgroundColor: theme.palette.background.ligth,
    boxShadow: `inset 2px 0 0 ${theme.accent.main}`,
    '&:hover': {
      backgroundColor: theme.palette.background.ligth,
    },
    '& $titleRole': {
      color: theme.palette.primary.darker,
    },
  },
  titleRole: {
    minWidth: 0,
    overflow: 'hidden',
    color: theme.palette.text.secondary,
    fontWeight: 500,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  countChip: {
    flexShrink: 0,
    height: 22,
    minWidth: 24,
    borderRadius: 11,
    backgroundColor: theme.palette.button.default,
    color: theme.palette.primary.darker,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 500,
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0.75),
      fontFamily:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontFeatureSettings: '"tnum"',
      fontVariantNumeric: 'tabular-nums',
    },
  },
  rule: {
    flex: 1,
    height: 1,
    backgroundColor: theme.palette.divider,
  },
  chevron: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    width: theme.spacing(2),
    textAlign: 'center',
  },
  empty: {
    color: theme.palette.text.secondary,
  },
}));

interface PartyListRoleProps {
  parties: PartyList[];
  handleDeleteAssociation: (partyId: string, kind: string, onSuccess?: () => void) => void;
  listingEntries: ListingEntry[];
  handleViewChange: (id: string) => void;
  variant?: 'card' | 'row';
  selectedId?: string;
  isDeleting?: boolean;
}

const PartyListRole: FC<PartyListRoleProps> = ({
  parties,
  handleDeleteAssociation,
  listingEntries,
  handleViewChange,
  variant = 'row',
  selectedId,
  isDeleting = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<string[]>(() =>
    parties.map((group) => group.kind)
  );

  const allCollapsed = parties.length > 0 && parties.every((group) => collapsed.includes(group.kind));

  const toggleRole = (kind: string) => {
    setCollapsed((current) =>
      current.includes(kind) ? current.filter((item) => item !== kind) : [...current, kind]
    );
  };

  const toggleAll = () => {
    setCollapsed(allCollapsed ? [] : parties.map((group) => group.kind));
  };

  return (
    <Box>
      {parties.length > 0 && (
        <Box className={classes.toolbar}>
          <BaseButton
            buttonVariant="text"
            buttonColor="inherit"
            icon={allCollapsed ? faChevronDown : faChevronUp}
            text={allCollapsed ? t('parties:expand-all') : t('parties:collapse-all')}
            onClick={toggleAll}
            customStyle={{ minHeight: 40 }}
          />
        </Box>
      )}
      {parties.map((party) => {
        const isCollapsed = collapsed.includes(party.kind);
        const hasSelectedParty =
          !!selectedId && party.parties.some((item) => item.id === selectedId);
        const panelId = `party-role-panel-${party.kind
          .replace(/[^a-zA-Z0-9]+/g, '-')
          .toLowerCase()}`;

        return (
          <Box key={party.kind} className={classes.group}>
            <ButtonBase
              className={clsx(classes.header, hasSelectedParty && classes.headerSelected)}
              onClick={() => toggleRole(party.kind)}
              aria-expanded={!isCollapsed}
              aria-controls={panelId}
              aria-current={hasSelectedParty ? 'true' : undefined}
              aria-label={
                isCollapsed
                  ? t('parties:expand-role', { role: formatRoleName(party.kind) })
                  : t('parties:collapse-role', { role: formatRoleName(party.kind) })
              }
            >
              <Typography variant="caption" component="span" className={classes.titleRole}>
                {formatRoleName(party.kind)}
              </Typography>
              <Box className={classes.rule} />
              <Chip
                size="small"
                label={party.parties.length}
                className={classes.countChip}
              />
              <FontAwesomeIcon
                icon={isCollapsed ? faChevronRight : faChevronDown}
                className={classes.chevron}
              />
            </ButtonBase>
            <Box id={panelId} hidden={isCollapsed}>
              {!isCollapsed &&
                (party.parties.length > 0 ? (
                  <PartyItem
                    parties={party.parties}
                    handleDeleteAssociation={handleDeleteAssociation}
                    kind={party.kind}
                    listingEntries={listingEntries}
                    handleViewChange={handleViewChange}
                    variant={variant}
                    selectedId={selectedId}
                    isDeleting={isDeleting}
                  />
                ) : (
                  <Typography variant="body2" className={classes.empty}>
                    {t('parties:no-parties-group')}
                  </Typography>
                ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default PartyListRole;
